import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { RutaDiaContenido } from "../admin/SeguimientoRutaSupervisora";
import { getServicioStyle } from "../../utils/servicioColores";

// RUTA (semanal / mensual) de la supervisora. Supervisora: «Mi Ruta» (solo sus pisos). Superadmin y admins: «Ruta de supervisora» (con selector de supervisora): para cada piso, qué días le tocaba visita según su
// ruta (R1: lunes y jueves · R2: martes y viernes · R3: el día organizado en «Organizar Ruta 3», miércoles si no) y cuáles ha visitado de verdad
// (visita = reporte de supervisora hasta el 29/10/2026; fichaje de entrada en el piso desde el 30/10). Arriba, lo que pide atención: pisos sin visitar y pisos de
// R3 visitados más de una vez.

const RUTAS = {
  1: { txt: "Ruta 1", dias: "lunes y jueves", color: "#2563eb", bg: "#eff6ff" },
  2: { txt: "Ruta 2", dias: "martes y viernes", color: "#7c3aed", bg: "#f5f3ff" },
  3: { txt: "Ruta 3", dias: "día organizado por piso (miércoles si no)", color: "#0d9488", bg: "#f0fdfa" },
};
const DIAS_SEMANA = ["L", "M", "X", "J", "V", "S", "D"];
const DIAS_LARGOS = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
const DIAS_NOMBRE = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

const p2 = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
const deIso = (s) => new Date(s + "T00:00:00");
const sumarDias = (s, n) => { const d = deIso(s); d.setDate(d.getDate() + n); return iso(d); };
const dowLunes = (s) => (deIso(s).getDay() + 6) % 7; // 0 = lunes
const fmt = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;
const fmtDia = (s) => `${DIAS_LARGOS[dowLunes(s)]} ${fmt(s)}`;
const lunesDe = (s) => sumarDias(s, -dowLunes(s));
const semanaISO = (s) => {
  const d = deIso(s); d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const s1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - s1) / 86400000 - 3 + ((s1.getDay() + 6) % 7)) / 7);
};
const rangoSemana = (ref) => { const l = lunesDe(ref); return { desde: l, hasta: sumarDias(l, 6) }; };
const rangoMes = (ref) => {
  const d = deIso(ref);
  return { desde: iso(new Date(d.getFullYear(), d.getMonth(), 1)), hasta: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
};

const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

const ChipPlan = ({ servicio }) => {
  if (!servicio) return null;
  const sc = getServicioStyle(servicio);
  return <span title="Plan (servicio) del piso" style={{ ...st.chipInfo, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 800 }}>Plan {servicio}</span>;
};

const Ruta = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const esSupervisora = authUser?.tipo === "supervisora";
  const miUsuario = authUser?.user || "";
  const hoyLocal = iso(new Date());
  const [modo, setModo] = useState("dia");   // dia | semana | mes
  const [ref, setRef] = useState(hoyLocal);    // cualquier día dentro del periodo mostrado
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [rutaSel, setRutaSel] = useState("todas");
  const [supSel, setSupSel] = useState("todas"); // solo admins: filtra por supervisora
  const [filtroEstado, setFiltroEstado] = useState("todos"); // todos | cero (0 visitas) | parcial (visitas pendientes)
  const [busca, setBusca] = useState("");
  const [detalle, setDetalle] = useState(null); // {piso, dia, visitas}

  const esDia = modo === "dia"; // el modo Día muestra el panel diario (paradas con hora y retraso)
  const { desde, hasta } = modo === "mes" ? rangoMes(ref) : rangoSemana(ref);

  useEffect(() => {
    if (modo === "dia") return undefined;
    let vivo = true;
    setCargando(true); setError("");
    axios.get("/api/reporte-supervisoras/ruta", { params: { fecha_inicio: desde, fecha_fin: hasta } })
      .then((r) => { if (vivo) setDatos(r.data); })
      .catch((e) => { if (vivo) setError(e.response?.data?.error || "No se pudo cargar la ruta."); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [desde, hasta, modo]);

  useEffect(() => {
    if (!detalle) return undefined;
    const esc = (e) => { if (e.key === "Escape") setDetalle(null); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [detalle]);

  const hoy = datos?.hoy || hoyLocal;
  const dias = useMemo(() => {
    const out = [];
    for (let d = desde; d <= hasta; d = sumarDias(d, 1)) out.push(d);
    return out;
  }, [desde, hasta]);

  const mover = (delta) => {
    if (modo === "semana") setRef(sumarDias(ref, 7 * delta));
    else { const d = deIso(ref); setRef(iso(new Date(d.getFullYear(), d.getMonth() + delta, 1))); }
  };
  const esActual = modo === "semana" ? lunesDe(ref) === lunesDe(hoyLocal) : ref.slice(0, 7) === hoyLocal.slice(0, 7);
  const titulo = modo === "semana"
    ? `Semana ${semanaISO(desde)} · ${+desde.slice(8, 10)} ${MESES_CORTOS[+desde.slice(5, 7) - 1]} – ${+hasta.slice(8, 10)} ${MESES_CORTOS[+hasta.slice(5, 7) - 1]} ${hasta.slice(0, 4)}`
    : `${MESES[+desde.slice(5, 7) - 1]} ${desde.slice(0, 4)}`;

  // análisis por piso: qué tocaba, qué se hizo, qué falta y qué sobra
  const supervisoras = useMemo(() => [...new Set((datos?.pisos || []).map((p) => p.user_supervisora).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")), [datos]);
  const pisos = useMemo(() => (datos?.pisos || []).filter((p) => esSupervisora || supSel === "todas" || p.user_supervisora === supSel).map((p) => {
    const porDia = {};
    p.visitas.forEach((v) => { (porDia[v.fecha] = porDia[v.fecha] || []).push(v); });
    const esp = new Set(p.esperados);
    const nVisitas = p.visitas.length;
    const visitadosSuDia = p.esperados.filter((d) => porDia[d]).length;
    const pasadosSinVisita = p.esperados.filter((d) => d < hoy && !porDia[d]);   // ya pasó y ese día no se fue
    // las visitas en OTRO día (o repetidas) cubren los días pendientes más antiguos: tocaba el miércoles y se fue el jueves = 1/1
    const compensados = pasadosSinVisita.slice(0, Math.max(0, nVisitas - visitadosSuDia));
    const faltan = pasadosSinVisita.slice(compensados.length);                  // lo que de verdad falta
    const hoyPendiente = p.esperados.includes(hoy) && !porDia[hoy];
    const extra = p.visitas.filter((v) => !esp.has(v.fecha));
    const vencidos = p.esperados.filter((d) => d < hoy || (d === hoy && porDia[d])).length;
    return { ...p, porDia, esp, faltan, compensados: new Set(compensados), hoyPendiente, extra, vencidos, nVisitas,
      cumplidas: Math.min(nVisitas, vencidos), hechas: Math.min(nVisitas, p.esperados.length),
      ceroVisitas: nVisitas === 0 && faltan.length > 0, parcial: nVisitas > 0 && faltan.length > 0 };
  }), [datos, hoy, esSupervisora, supSel]);

  // «Sin visitar» = el piso ha tenido CERO visitas en el periodo (y le tocaba alguna ya pasada); «pendientes» = ha ido pero le faltan visitas
  // Los números (chips y paneles) siguen la ruta elegida: Todas / Ruta 1 / 2 / 3
  const pisosRuta = useMemo(() => pisos.filter((p) => rutaSel === "todas" || String(p.ruta) === rutaSel), [pisos, rutaSel]);
  const ordenFaltan = (a, b) => (b.faltan.length - a.faltan.length) || String(a.abreviatura).localeCompare(String(b.abreviatura), "es", { numeric: true });
  const sinVisitar = useMemo(() => pisosRuta.filter((p) => p.ceroVisitas).sort(ordenFaltan), [pisosRuta]);
  const pendientes = useMemo(() => pisosRuta.filter((p) => p.parcial).sort(ordenFaltan), [pisosRuta]);
  const nParcial = pendientes.length;
  // R3 solo toca un día a la semana: «más de una vez» se mide POR SEMANA (lunes a domingo), también en la vista mensual
  const r3Repetidos = useMemo(() => pisos.filter((p) => p.ruta === 3).map((p) => {
    const porSemana = {};
    p.visitas.forEach((v) => { (porSemana[lunesDe(v.fecha)] = porSemana[lunesDe(v.fecha)] || []).push(v); });
    const semanas = Object.entries(porSemana).filter(([, vs]) => vs.length > 1).sort(([a], [b]) => a.localeCompare(b))
      .map(([lunes, vs]) => ({ lunes, num: semanaISO(lunes), visitas: vs }));
    return { ...p, semanasRepetidas: semanas };
  }).filter((p) => p.semanasRepetidas.length)
    .sort((a, b) => b.semanasRepetidas.length - a.semanasRepetidas.length || String(a.abreviatura).localeCompare(String(b.abreviatura), "es", { numeric: true })), [pisos]);

  const res = datos?.resumen && !(supSel !== "todas" && !esSupervisora) ? datos.resumen : null;   // el resumen es de todo lo cargado: con filtro de supervisora no se enseña
  const kpi = useMemo(() => {
    const venc = pisos.reduce((a, p) => a + p.vencidos, 0);
    const hechas = pisos.reduce((a, p) => a + p.cumplidas, 0);
    return { pisos: pisos.length, venc, hechas, pct: venc ? Math.round((hechas / venc) * 100) : null, extra: pisos.reduce((a, p) => a + p.extra.length, 0) };
  }, [pisos]);

  const grupos = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return [1, 2, 3].filter((r) => rutaSel === "todas" || String(r) === rutaSel).map((r) => ({
      ruta: r,
      filas: pisos.filter((p) => p.ruta === r && (filtroEstado === "todos" || (filtroEstado === "cero" ? p.ceroVisitas : p.parcial))
        && (!q || `${p.abreviatura} ${p.asistenta || ""} ${p.servicio || ""}`.toLowerCase().includes(q)))
        .map((p) => {
          // Ruta 3: primer día que le toca en la ÚLTIMA semana con visita (por si cambió de días) y hora de entrada de la asistenta ese día
          const ult = p.esperados.length ? lunesDe(p.esperados[p.esperados.length - 1]) : null;
          const dia = ult ? Math.min(...p.esperados.filter((d) => lunesDe(d) === ult).map(dowLunes)) : 9;
          return { ...p, diaOrden: dia, horaOrden: p.horario?.[dia]?.[0] || "99:99" };
        })
        .sort((a, b) => (r === 3 ? (a.diaOrden - b.diaOrden || a.horaOrden.localeCompare(b.horaOrden)) : 0)
          || String(a.abreviatura).localeCompare(String(b.abreviatura), "es", { numeric: true })),
    })).filter((g) => g.filas.length);
  }, [pisos, rutaSel, filtroEstado, busca]);

  const compacto = dias.length > 7;
  const ancho = compacto ? 88 : 112;   // columnas anchas: cada celda enseña entrada, salida y reporte sin pulsar

  const celda = (p, d) => {
    const vs = p.porDia[d];
    const toca = p.esp.has(d);
    const base = { display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", fontWeight: 900, boxSizing: "border-box", width: compacto ? 20 : 34, height: compacto ? 20 : 34, fontSize: compacto ? 10.5 : 15 };
    if (vs && toca) return <span style={{ ...base, background: "#16a34a", color: "#fff", boxShadow: "0 0 0 2px #dcfce7" }}>{vs.length > 1 ? vs.length : "✓"}</span>;
    if (vs) return <span title="Visita fuera de su día" style={{ ...base, background: "#a855f7", color: "#fff", boxShadow: "0 0 0 2px #f3e8ff" }}>{vs.length > 1 ? vs.length : "+"}</span>;
    if (!toca) return null;
    if (p.compensados.has(d)) return <span title="Ese día no se fue, pero se cubrió con una visita en otro día" style={{ ...base, background: "#ecfdf5", color: "#15803d", border: "2px dashed #86efac" }}>≈</span>;
    if (d < hoy) return <span style={{ ...base, background: "#fee2e2", color: "#dc2626", border: "2px solid #fca5a5" }}>✕</span>;
    if (d === hoy) return <span style={{ ...base, background: "#fef3c7", color: "#b45309", border: "2px dashed #f59e0b", fontSize: compacto ? 9 : 12 }}>hoy</span>;
    return <span style={{ ...base, border: "2px dashed #cbd5e1", color: "#cbd5e1" }} />;
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "Segoe UI, sans-serif" }}>
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={st.btnCab}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Servicios · visitas a pisos según la ruta</div>
          <strong style={{ fontSize: 20 }}>🗺️ {esSupervisora ? "Mi Ruta" : "Ruta de supervisora"}</strong>
        </div>
      </div>

      <div style={{ padding: "14px 16px", maxWidth: 1500, margin: "0 auto" }}>
        {/* Periodo */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "inline-flex", background: "#fff", border: "2px solid #c7d2fe", borderRadius: 999, padding: 3 }}>
            {[["dia", "☀️ Día"], ["semana", "📅 Semana"], ["mes", "🗓️ Mes"]].map(([k, t]) => (
              <button key={k} onClick={() => setModo(k)} style={{ ...st.pill, ...(modo === k ? st.pillOn : {}) }}>{t}</button>
            ))}
          </div>
          {!esDia && (<>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", borderRadius: 12, border: "2px solid #c7d2fe", padding: "4px 6px" }}>
            <button onClick={() => mover(-1)} title="Anterior" style={st.flecha}>◀</button>
            <strong style={{ minWidth: modo === "semana" ? 250 : 150, textAlign: "center", color: "#1e293b", fontSize: 14, textTransform: "capitalize" }}>{titulo}</strong>
            <button onClick={() => mover(1)} title="Siguiente" style={st.flecha}>▶</button>
          </div>
          {!esActual && <button onClick={() => setRef(hoyLocal)} style={st.btnHoy}>↩ {modo === "semana" ? "Esta semana" : "Este mes"}</button>}
          {!esSupervisora && supervisoras.length > 0 && (
            <select value={supSel} onChange={(e) => setSupSel(e.target.value)} style={st.select}>
              <option value="todas">👩‍💼 Todas las supervisoras</option>
              {supervisoras.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          )}
          {cargando && <span style={{ color: "#64748b", fontSize: 13 }}>Cargando…</span>}
          </>)}
        </div>

        {esDia ? (
          <RutaDiaContenido
            supervisoraFija={esSupervisora ? miUsuario : null}
            supervisora={supSel === "todas" ? "" : supSel}
            onSupervisora={setSupSel}
          />
        ) : (<>
        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: 12, marginBottom: 12 }}>{error}</div>}

        {/* Resumen */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          {[
            { t: esSupervisora ? "Pisos en tu ruta" : "Pisos", v: kpi.pisos, c: "#1e3a5f", bg: "#fff" },
            { t: "Visitas hechas / tocaban", v: datos ? `${kpi.hechas} / ${kpi.venc}` : "…", c: "#15803d", bg: "#dcfce7" },
            { t: "Cumplimiento", v: datos ? (kpi.pct == null ? "—" : `${kpi.pct}%`) : "…", c: kpi.pct != null && kpi.pct < 80 ? "#b91c1c" : "#15803d", bg: kpi.pct != null && kpi.pct < 80 ? "#fee2e2" : "#f0fdf4" },
            { t: "Visitas extra (otro día)", v: kpi.extra, c: "#7e22ce", bg: "#f3e8ff" },
            ...(res ? [
              { t: "🟢 A tiempo", v: res.a_tiempo, c: "#15803d", bg: "#dcfce7" },
              { t: "🔴 Fuera de tiempo", v: res.fuera_tiempo, c: "#b91c1c", bg: "#fee2e2" },
              { t: "Retraso medio", v: res.retraso_medio_minutos != null ? `${res.retraso_medio_minutos} min` : "—", c: "#b45309", bg: "#fef3c7" },
              ...(res.reportes_hechos != null ? [
                { t: "📝 Reportes hechos", v: res.reportes_hechos, c: "#15803d", bg: "#dcfce7" },
                { t: "⏳ Reportes retrasados", v: res.reportes_retrasados, c: "#b91c1c", bg: "#fee2e2" },
                { t: "Visitas sin reporte", v: res.visitas_sin_reporte, c: "#b91c1c", bg: "#fee2e2" },
                { t: "🏠 Tiempo en pisos", v: res.minutos_en_pisos != null ? fmtMin(res.minutos_en_pisos) : "—", c: "#1d4ed8", bg: "#eff6ff" },
                { t: "🚶 Desplazamiento medio", v: res.desplazamiento_medio_min != null ? fmtMin(res.desplazamiento_medio_min) : "—", c: "#475569", bg: "#f1f5f9" },
              ] : []),
            ] : []),
          ].map((k) => (
            <div key={k.t} style={{ background: k.bg, borderRadius: 14, padding: "10px 16px", minWidth: 150, border: "1px solid #e2e8f0" }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: k.c }}>{k.t}</div>
              <div style={{ fontSize: 26, fontWeight: 900, color: "#1e293b" }}>{datos ? k.v : "…"}</div>
            </div>
          ))}
        </div>

        {/* Lo que pide atención */}
        {datos && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 12, marginBottom: 14 }}>
            <div style={{ ...st.panel, borderColor: sinVisitar.length ? "#fca5a5" : "#bbf7d0", background: sinVisitar.length ? "#fff7f7" : "#f0fdf4" }}>
              <div style={{ ...st.panelTit, color: sinVisitar.length ? "#b91c1c" : "#15803d" }}>
                {sinVisitar.length ? `🚫 Pisos sin visitar (${sinVisitar.length})` : "✅ Ningún piso sin visitar"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", margin: "-4px 0 6px" }}>Pisos a los que no se ha ido <b>ninguna vez</b> en este periodo.</div>
              {sinVisitar.length === 0
                ? <div style={st.vacio}>Ningún piso con cero visitas entre los días ya pasados.</div>
                : (
                  <div style={st.lista}>
                    {sinVisitar.map((p) => (
                      <div key={p.id} onClick={() => { setBusca(p.abreviatura); setFiltroEstado("todos"); }} style={st.item} title="Pulsa para verlo en la tabla">
                        <strong style={{ color: "#1e293b" }}>{p.abreviatura}</strong>
                        <span style={{ ...st.chipRuta, color: RUTAS[p.ruta].color, background: RUTAS[p.ruta].bg }}>R{p.ruta}</span>
                        <ChipPlan servicio={p.servicio} />
                        <span style={st.chipNinguna}>0 visitas</span>
                        <span style={{ marginLeft: "auto", display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                          {p.faltan.map((d) => <span key={d} style={st.chipFalta}>{fmtDia(d)}</span>)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <div style={{ ...st.panel, borderColor: pendientes.length ? "#fde68a" : "#bbf7d0", background: pendientes.length ? "#fffbeb" : "#f0fdf4" }}>
              <div style={{ ...st.panelTit, color: pendientes.length ? "#b45309" : "#15803d" }}>
                {pendientes.length ? `⏳ Pisos con visitas pendientes (${pendientes.length})` : "✅ Ningún piso con visitas pendientes"}
              </div>
              <div style={{ fontSize: 12, color: "#64748b", margin: "-4px 0 6px" }}>Pisos a los que se ha ido, pero <b>les falta alguna visita</b> de los días ya pasados.</div>
              {pendientes.length === 0
                ? <div style={st.vacio}>Ningún piso con visitas pendientes entre los días ya pasados.</div>
                : (
                  <div style={st.lista}>
                    {pendientes.map((p) => (
                      <div key={p.id} onClick={() => { setBusca(p.abreviatura); setFiltroEstado("todos"); }} style={st.item} title="Pulsa para verlo en la tabla">
                        <strong style={{ color: "#1e293b" }}>{p.abreviatura}</strong>
                        <span style={{ ...st.chipRuta, color: RUTAS[p.ruta].color, background: RUTAS[p.ruta].bg }}>R{p.ruta}</span>
                        <ChipPlan servicio={p.servicio} />
                        <span style={st.chipVeces}>{p.nVisitas} visita{p.nVisitas !== 1 ? "s" : ""}</span>
                        <span style={{ marginLeft: "auto", display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                          {p.faltan.map((d) => <span key={d} style={st.chipFalta}>{fmtDia(d)}</span>)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <div style={{ ...st.panel, borderColor: r3Repetidos.length ? "#ddd6fe" : "#e2e8f0", background: r3Repetidos.length ? "#faf5ff" : "#fff" }}>
              <div style={{ ...st.panelTit, color: "#6d28d9" }}>🔁 Ruta 3 visitados más de una vez por semana ({r3Repetidos.length})</div>
              {r3Repetidos.length === 0
                ? <div style={st.vacio}>Ningún piso de ruta 3 se ha visitado más de una vez en una misma semana.</div>
                : (
                  <div style={st.lista}>
                    {r3Repetidos.map((p) => (
                      <div key={p.id} onClick={() => { setBusca(p.abreviatura); setRutaSel("todas"); }} style={{ ...st.item, alignItems: "flex-start" }} title="Pulsa para verlo en la tabla">
                        <strong style={{ color: "#1e293b" }}>{p.abreviatura}</strong>
                        <span style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 5, alignItems: "flex-end" }}>
                          {p.semanasRepetidas.map((w) => (
                            <span key={w.lunes} style={{ display: "inline-flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                              <span style={st.chipVeces}>{modo === "mes" ? `Sem. ${w.num} · ` : ""}{w.visitas.length} visitas</span>
                              {w.visitas.map((v, i) => <span key={i} style={st.chipVisita}>{fmtDia(v.fecha)} · {v.hora}</span>)}
                            </span>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Filtros + leyenda */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 8 }}>
          {[["todas", "Todas las rutas"], ["1", "Ruta 1"], ["2", "Ruta 2"], ["3", "Ruta 3"]].map(([k, t]) => (
            <button key={k} onClick={() => setRutaSel(k)} style={{ ...st.chip, ...(rutaSel === k ? st.chipOn : {}) }}>{t}</button>
          ))}
          <span style={{ width: 1, height: 26, background: "#cbd5e1", margin: "0 2px" }} />
          {[["todos", "Todos", pisosRuta.length, "#4338ca"], ["cero", "🚫 Sin visitar", sinVisitar.length, "#dc2626"], ["parcial", "⏳ Visitas pendientes", nParcial, "#d97706"]].map(([k, t, n, c]) => {
            const act = filtroEstado === k;
            return (
              <button key={k} onClick={() => setFiltroEstado(k)} title={k === "cero" ? "Pisos a los que no se ha ido ninguna vez" : k === "parcial" ? "Pisos a los que se ha ido pero les falta alguna visita" : "Todos los pisos"}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                  border: `2px solid ${act ? c : "#e2e8f0"}`, background: act ? c : "#fff", color: act ? "#fff" : c }}>
                {t}
                <span style={{ background: act ? "rgba(255,255,255,0.25)" : `${c}1a`, color: act ? "#fff" : c, borderRadius: 999, padding: "0 7px", fontSize: 11.5 }}>{n}</span>
              </button>
            );
          })}
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar piso o asistenta…"
            style={{ flex: 1, minWidth: 190, padding: "8px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, outline: "none" }} />
          {busca && <button onClick={() => setBusca("")} style={{ ...st.chip, borderColor: "#fca5a5", color: "#b91c1c" }}>✕ Quitar búsqueda</button>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12.5, color: "#475569", marginBottom: 8, alignItems: "center" }}>
          {[
            [<span key="a" style={{ ...st.mini, background: "#16a34a", color: "#fff" }}>✓</span>, "Visitado su día"],
            [<span key="b" style={{ ...st.mini, background: "#fee2e2", color: "#dc2626", border: "2px solid #fca5a5" }}>✕</span>, "Tocaba y no se visitó"],
            [<span key="c" style={{ ...st.mini, border: "2px dashed #f59e0b", background: "#fef3c7" }} />, "Toca hoy"],
            [<span key="d" style={{ ...st.mini, border: "2px dashed #cbd5e1" }} />, "Pendiente"],
            [<span key="e" style={{ ...st.mini, background: "#a855f7", color: "#fff" }}>+</span>, "Visita en otro día"],
            [<span key="f" style={{ ...st.mini, background: "#ecfdf5", color: "#15803d", border: "2px dashed #86efac" }}>≈</span>, "Cubierta con una visita de otro día"],
          ].map(([ic, t], i) => <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>{ic}{t}</span>)}
          <span style={{ color: "#94a3b8" }}>📝✅ con reporte · 📝❌ sin reporte · 📝⚠️ reporte más de 10 min después de la salida · pulsa el círculo para ver las horas · un número = varias visitas ese día</span>
        </div>

        {/* Tabla piso × día */}
        {cargando && !datos ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Cargando…</div> : (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "auto", maxHeight: "70vh", opacity: cargando ? 0.55 : 1 }}>
            <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12.5, minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...st.th, left: 0, zIndex: 4, minWidth: 230, textAlign: "left" }}>Piso</th>
                  {dias.map((d) => {
                    const finde = dowLunes(d) >= 5;
                    return (
                      <th key={d} style={{ ...st.th, minWidth: ancho, padding: "5px 0", background: d === hoy ? "#dbeafe" : finde ? "#e2e8f0" : "#f1f5f9", color: d === hoy ? "#1e3a5f" : "#64748b",
                        borderLeft: d.slice(8, 10) === "01" || dowLunes(d) === 0 ? "2px solid #cbd5e1" : undefined }}>
                        <div style={{ fontSize: compacto ? 9 : 11 }}>{compacto ? DIAS_SEMANA[dowLunes(d)] : DIAS_LARGOS[dowLunes(d)]}</div>
                        <div style={{ fontSize: compacto ? 10.5 : 13, fontWeight: 900 }}>{compacto ? +d.slice(8, 10) : fmt(d)}</div>
                      </th>
                    );
                  })}
                  <th style={{ ...st.th, minWidth: 64 }}>Hechas</th>
                </tr>
              </thead>
              <tbody>
                {grupos.length === 0 && (
                  <tr><td colSpan={dias.length + 2} style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>
                    {datos && datos.pisos.length === 0 ? (esSupervisora ? "No tienes pisos con ruta asignada." : "No hay pisos con ruta asignada.") : "No hay pisos con estos filtros."}
                  </td></tr>
                )}
                {grupos.map((g) => (
                  <React.Fragment key={g.ruta}>
                    <tr>
                      <td colSpan={dias.length + 2} style={{ position: "sticky", left: 0, background: RUTAS[g.ruta].bg, color: RUTAS[g.ruta].color, fontWeight: 900, padding: "7px 10px", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
                        {RUTAS[g.ruta].txt} <span style={{ fontWeight: 600, opacity: 0.8 }}>· {RUTAS[g.ruta].dias} · {g.filas.length} piso{g.filas.length !== 1 ? "s" : ""}</span>
                      </td>
                    </tr>
                    {g.filas.map((p, i) => {
                      const fondo = i % 2 ? "#fafafa" : "#fff";
                      const ok = p.faltan.length === 0;
                      const nuevoDia = g.ruta === 3 && (i === 0 || g.filas[i - 1].diaOrden !== p.diaOrden);
                      const hor = p.horario?.[p.diaOrden];          // horario de la asistenta el día que le toca
                      const sc = p.servicio ? getServicioStyle(p.servicio) : null;
                      return (
                        <React.Fragment key={p.id}>
                        {nuevoDia && (
                          <tr>
                            <td colSpan={dias.length + 2} style={{ position: "sticky", left: 0, background: "#f0fdfa", color: "#0f766e", fontWeight: 800, padding: "4px 10px", fontSize: 12, borderBottom: "1px solid #ccfbf1" }}>
                              {p.diaOrden < 7 ? DIAS_NOMBRE[p.diaOrden] : "Sin día"} <span style={{ fontWeight: 600, opacity: 0.8 }}>· {g.filas.filter((x) => x.diaOrden === p.diaOrden).length} · por hora de entrada de la asistenta</span>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ ...st.td, position: "sticky", left: 0, zIndex: 2, background: fondo, borderRight: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                            <div style={{ fontWeight: 800, color: "#1e293b" }}>{p.abreviatura}</div>
                            {(!esSupervisora && supSel === "todas" ? p.user_supervisora : p.asistenta) && <div style={{ fontSize: 10.5, color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{!esSupervisora && supSel === "todas" ? p.user_supervisora : p.asistenta}</div>}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", marginTop: 2 }}>
                              <span title="Horario de la asistenta (entrada–salida)" style={st.chipInfo}>🕐 {hor ? `${hor[0] || "--"}–${hor[1] || "--"}` : "sin horario"}</span>
                              {sc && <span title="Plan (servicio) del piso" style={{ ...st.chipInfo, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 800 }}>Plan {p.servicio}</span>}
                              <span title="Alumnos del piso: A / R" style={st.chipInfo}>👥 <b style={{ color: "#1d4ed8" }}>A {p.alumnos?.a ?? 0}</b> · <b style={{ color: "#b45309" }}>R {p.alumnos?.r ?? 0}</b></span>
                            </div>
                          </td>
                          {dias.map((d) => {
                            const vs = p.porDia[d];
                            return (
                              <td key={d} onClick={vs ? () => setDetalle({ piso: p, dia: d, visitas: vs }) : undefined}
                                style={{ ...st.td, padding: 0, textAlign: "center", cursor: vs ? "pointer" : "default",
                                  background: d === hoy ? "#eff6ff" : dowLunes(d) >= 5 ? "#f8fafc" : fondo,
                                  borderLeft: d.slice(8, 10) === "01" || dowLunes(d) === 0 ? "2px solid #e2e8f0" : "1px solid #f1f5f9" }}>
                                {celda(p, d)}
                                {vs && (
                                  <div style={{ fontSize: 13, lineHeight: 1.3, padding: "0 2px 4px", whiteSpace: "nowrap" }}>
                                    {/* sin horas: solo si hay reporte o no (las horas salen al pulsar el círculo) */}
                                    {vs.map((v, vi) => v.origen === "reporte"
                                      ? <div key={vi} title="Visita registrada por reporte" style={{ color: "#7c3aed" }}>📝</div>
                                      : (v.reportes || []).length
                                        ? <div key={vi} title={v.reportes.some((r) => r.fuera) ? "Reporte enviado más de 10 min después de la salida" : "Con reporte"}>{v.reportes.some((r) => r.fuera) ? "📝⚠️" : "📝✅"}</div>
                                        : <div key={vi} title="Sin reporte">📝❌</div>)}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                          <td style={{ ...st.td, textAlign: "center", fontWeight: 900, color: p.esperados.length === 0 ? "#cbd5e1" : ok ? "#15803d" : "#b91c1c" }}>
                            {p.esperados.length ? `${p.hechas}/${p.esperados.length}` : "—"}
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 6 }}>
          Una visita es un reporte de supervisora del piso hasta el 29/10/2026 y un fichaje de entrada de la supervisora en el piso (QR + ubicación) desde el 30/10. «Hechas» cuenta también las visitas hechas en otro día (si tocaba el miércoles y se fue el jueves, es 1/1), sobre las que tocaban en el periodo completo.
        </div>
        </>)}
      </div>

      {detalle && (
        <div onClick={() => setDetalle(null)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 2500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "min(420px, 100%)", padding: "16px 18px", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: "#1e3a5f" }}>🏠 {detalle.piso.abreviatura}</div>
                <div style={{ fontSize: 13, color: "#64748b" }}>{fmtDia(detalle.dia)} · {detalle.piso.esp.has(detalle.dia) ? "día de su ruta" : "fuera de su día"}</div>
              </div>
              <button onClick={() => setDetalle(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 800 }}>✕</button>
            </div>
            {detalle.visitas.map((v, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4, border: "1px solid #eef2f7", borderRadius: 12, padding: "10px 14px", marginBottom: 6 }}>
                {v.origen === "reporte"
                  ? <strong style={{ fontSize: 16, color: "#1e293b" }}>🕐 {v.hora} <span style={{ fontSize: 12.5, fontWeight: 600, color: "#64748b" }}>(reporte)</span></strong>
                  : (<>
                    <strong style={{ fontSize: 16, color: "#15803d" }}>▶ Entrada {v.hora}</strong>
                    {v.minutos != null && <span style={{ alignSelf: "flex-start", fontSize: 14, fontWeight: 800, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 999, padding: "2px 12px" }}>🏠 {v.minutos >= 60 ? `${Math.floor(v.minutos / 60)}h ${v.minutos % 60}m` : `${v.minutos}m`} en el piso</span>}
                    <strong style={{ fontSize: 16, color: v.hora_salida ? "#1d4ed8" : "#b45309" }}>⏹ {v.hora_salida ? `Salida ${v.hora_salida}` : "sin fichar salida"}</strong>
                    {(v.reportes || []).length
                      ? v.reportes.map((r, ri) => <span key={ri} style={{ color: r.fuera ? "#dc2626" : "#7c3aed", fontWeight: 700, fontSize: 14 }}>📝 Reporte {r.hora}{r.fuera ? " ⚠️ fuera del fichaje" : ""}</span>)
                      : <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 14 }}>📝 sin reporte</span>}
                  </>)}
                <span style={{ color: "#475569", fontSize: 13.5 }}>👤 {v.usuario}</span>
                {v.sustituta && <span style={{ alignSelf: "flex-start", fontSize: 11.5, fontWeight: 800, color: "#b45309", background: "#fef3c7", borderRadius: 999, padding: "2px 8px" }}>otra supervisora</span>}
              </div>
            ))}
            {detalle.visitas.length > 1 && <div style={{ fontSize: 12.5, color: "#6d28d9", marginTop: 4 }}>🔁 {detalle.visitas.length} visitas el mismo día</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const st = {
  btnCab: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "8px 14px" },
  pill: { border: "none", background: "transparent", borderRadius: 999, padding: "7px 16px", fontSize: 13.5, fontWeight: 800, cursor: "pointer", color: "#3730a3" },
  pillOn: { background: "#4338ca", color: "#fff" },
  flecha: { border: "none", background: "#eef2ff", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontWeight: 900, color: "#3730a3" },
  select: { padding: "9px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, fontWeight: 700, color: "#3730a3", background: "#fff", cursor: "pointer", outline: "none" },
  btnHoy: { border: "2px solid #4338ca", background: "#fff", color: "#4338ca", borderRadius: 999, padding: "6px 14px", fontSize: 13, fontWeight: 800, cursor: "pointer" },
  panel: { border: "2px solid", borderRadius: 14, padding: "12px 14px" },
  panelTit: { fontSize: 15, fontWeight: 900, marginBottom: 8 },
  vacio: { fontSize: 13, color: "#64748b", padding: "4px 0" },
  lista: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 250, overflowY: "auto" },
  item: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", background: "#fff", border: "1px solid #eef2f7", borderRadius: 10, padding: "7px 10px", cursor: "pointer", fontSize: 13.5 },
  chipRuta: { fontSize: 11, fontWeight: 900, borderRadius: 6, padding: "1px 6px" },
  chipNinguna: { fontSize: 11, fontWeight: 800, color: "#fff", background: "#dc2626", borderRadius: 999, padding: "1px 8px" },
  chipFalta: { fontSize: 11.5, fontWeight: 800, color: "#b91c1c", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" },
  chipVeces: { fontSize: 11, fontWeight: 900, color: "#fff", background: "#7c3aed", borderRadius: 999, padding: "1px 8px" },
  chipVisita: { fontSize: 11.5, fontWeight: 700, color: "#5b21b6", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 999, padding: "1px 8px", whiteSpace: "nowrap" },
  chipInfo: { fontSize: 10.5, fontWeight: 700, color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 999, padding: "0 7px", whiteSpace: "nowrap" },
  chip: { border: "2px solid #c7d2fe", background: "#fff", color: "#3730a3", borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  chipOn: { background: "#4338ca", borderColor: "#4338ca", color: "#fff" },
  mini: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: "50%", fontSize: 10, fontWeight: 900, boxSizing: "border-box" },
  th: { position: "sticky", top: 0, zIndex: 3, background: "#f1f5f9", color: "#64748b", fontSize: 11.5, fontWeight: 700, padding: "8px 8px", borderBottom: "1.5px solid #e2e8f0", textAlign: "center", whiteSpace: "nowrap" },
  td: { padding: "5px 8px", borderBottom: "1px solid #f1f5f9", height: 44, boxSizing: "border-box" },
};

export default Ruta;
